/* eslint-disable indent */
/**
 * @format
 */

import { Button, Flex, type ChakraProps } from "@chakra-ui/react"
import React, { useEffect, useState } from "react"
import { Colors } from "../theme/colors"

interface tabProps {
  /**
   * An array of tab texts.
   */
  tabs: string[]
  /**
   * The default tab to be selected.
   */
  defaultTabIndex: number
  /**
   * Click handler for the tabs. This call back returns two options a) the tab title b) the selected tab's index in the input array.
   */
  tabClickHandler: (tabType: string, tabIndex: number) => void
  /**
   * Any chakra style props to be applied for the tab container
   */
  tabContainerStyles?: ChakraProps
  tabButtonStyles?: ChakraProps
  /**
   * Optional aria-label for the tab list
   */
  ariaLabel?: string
}

export const Tabs = ({ tabs = [], defaultTabIndex = 0, tabClickHandler, tabContainerStyles, tabButtonStyles, ariaLabel }: tabProps): JSX.Element => {
  const [activeTabIndex, setActiveTabIndex] = useState<number>(0)

  useEffect(() => {
    if (tabs.length === 0) {
      console.error("tabs array cannot be empty")
    }

    if (defaultTabIndex > tabs.length - 1) {
      // if the default tab index is greater than the total number of tabs. Use the first tab.
      setActiveTabIndex(0)
    }

    setActiveTabIndex(defaultTabIndex)
  }, [])

  const handleTabClick = (index: number): void => {
    setActiveTabIndex(index)
    tabClickHandler(tabs[index], index)
  }

  const handleKeyDown = (event: React.KeyboardEvent, index: number): void => {
    switch (event.key) {
      case "Enter":
      case " ":
        event.preventDefault()
        handleTabClick(index)
        break
      case "ArrowRight": {
        event.preventDefault()
        const nextIndex = (index + 1) % tabs.length
        handleTabClick(nextIndex)
        break
      }
      case "ArrowLeft": {
        event.preventDefault()
        const prevIndex = index === 0 ? tabs.length - 1 : index - 1
        handleTabClick(prevIndex)
        break
      }
      case "Home":
        event.preventDefault()
        handleTabClick(0)
        break
      case "End":
        event.preventDefault()
        handleTabClick(tabs.length - 1)
        break
    }
  }

  return (
    <>
      {tabs?.length > 0 && (
        <Flex
          role="tablist"
          aria-label={ariaLabel ?? "Navigation tabs"}
          mb="4"
          borderBottomColor={Colors.Primary.accent}
          borderBottomWidth="1px"
          flexWrap={["nowrap", "nowrap", "wrap"]}
          width="100%"
          {...tabContainerStyles}
        >
          {tabs.map((obj: string, i) => (
            <Button
              role="tab"
              aria-selected={activeTabIndex === i}
              aria-controls={`panel-${i}`}
              id={`tab-${i}`}
              tabIndex={activeTabIndex === i ? 0 : -1}
              onClick={() => {
                handleTabClick(i)
              }}
              onKeyDown={e => {
                handleKeyDown(e, i)
              }}
              variant="linkButton"
              fontWeight={activeTabIndex === i ? "700" : "400"}
              data-testid={activeTabIndex === i ? "selected-tab" : ""}
              color={activeTabIndex === i ? Colors.Primary.accent : Colors.Secondary.c2}
              key={`${obj}_tabs_button`}
              ml={i === 0 ? "0" : ["4px", "4px", "19px"]}
              py="10px"
              pb={["12px", "12px", "16px"]}
              px={[1, 2, "5px"]}
              fontSize={["16px", "16px", "18px"]}
              fontFamily="PT Sans"
              minW={["120px", "80px", "160px"]}
              lineHeight="18px"
              sx={{
                borderBottom: activeTabIndex === i ? [`2px solid ${Colors.Primary.accent}`, `3px solid ${Colors.Primary.accent}`] : "none !important"
              }}
              _hover={{ backgroundColor: ["transparent", "transparent", "gray.50"] }}
              _focus={{
                outline: "2px solid",
                outlineColor: Colors.Primary.accent,
                outlineOffset: "2px"
              }}
              wordBreak="break-word"
              whiteSpace="break-spaces"
              textAlign={["left", "left", "center"]}
              {...tabButtonStyles}
            >
              {obj}
            </Button>
          ))}
        </Flex>
      )}
    </>
  )
}
